import { Icon } from '@dc-shared-web/components/base/Icon'
import {
  faAngleDown,
  faAngleUp,
  IconDefinition,
} from '@document-crunch/pro-regular-svg-icons'
import { cva, VariantProps } from 'class-variance-authority'
import { ButtonHTMLAttributes, forwardRef } from 'react'

const chipTriggerStyles = cva(
  'tw-inline-flex tw-items-center tw-gap-1 focus:tw-outline-none focus:tw-ring-2 tw-h-6 tw-px-2 tw-text-sm',
  {
    variants: {
      color: {
        default: '',
        blue: '',
        yellow: '',
        green: '',
        red: '',
      },
      variant: {
        default: 'tw-shadow-md tw-rounded-xl',
        surface: 'tw-border-2 tw-rounded-lg',
      },
    },
    compoundVariants: [
      {
        variant: 'default',
        color: 'default',
        className:
          'tw-bg-white tw-text-neutral-900 [&_svg]:tw-text-neutral-900',
      },
      {
        variant: 'surface',
        color: 'default',
        className: 'tw-bg-white tw-border-neutral-200',
      },
      {
        variant: 'default',
        color: 'blue',
        className:
          'tw-bg-blue-200 tw-text-neutral-900 [&_svg]:tw-text-neutral-900',
      },
      {
        variant: 'surface',
        color: 'blue',
        className:
          'tw-bg-blue-50 tw-border-blue-400 tw-text-blue-600 [&_svg]:tw-text-blue-600',
      },
      {
        variant: 'default',
        color: 'yellow',
        className:
          'tw-bg-yellow-200 tw-text-neutral-900 [&_svg]:tw-text-neutral-900',
      },
      {
        variant: 'surface',
        color: 'yellow',
        className:
          'tw-bg-yellow-50 tw-border-yellow-400 tw-text-yellow-600 [&_svg]:tw-text-yellow-600',
      },
      {
        variant: 'default',
        color: 'green',
        className:
          'tw-bg-green-100 tw-text-neutral-900 [&_svg]:tw-text-neutral-900',
      },
      {
        variant: 'surface',
        color: 'green',
        className:
          'tw-bg-green-50 tw-border-green-400 tw-text-green-700 [&_svg]:tw-text-green-700',
      },
      {
        variant: 'default',
        color: 'red',
        className:
          'tw-bg-red-200 tw-text-neutral-900 [&_svg]:tw-text-neutral-900',
      },
      {
        variant: 'surface',
        color: 'red',
        className:
          'tw-bg-red-50 tw-border-red-400 tw-text-red-600 [&_svg]:tw-text-red-600',
      },
    ],
    defaultVariants: {
      color: 'default',
      variant: 'default',
    },
  }
)

export type ChipTriggerProps = ButtonHTMLAttributes<HTMLButtonElement> &
  VariantProps<typeof chipTriggerStyles> & {
    /** Icon to be displayed in the chip trigger. */
    icon?: IconDefinition
    /** Whether the chip trigger is open. If true, the icon will be rotated. */
    isOpen?: boolean
  }

export const ChipTrigger = forwardRef<HTMLButtonElement, ChipTriggerProps>(
  ({ icon, children, color, variant, isOpen, ...rest }, ref) => {
    return (
      <button
        ref={ref}
        {...rest}
        className={chipTriggerStyles({ color, variant })}
      >
        {icon && <Icon icon={icon} size='sm' />}
        {children}
        <div className='tw-mt-0.5'>
          <Icon icon={isOpen ? faAngleUp : faAngleDown} size='sm' />
        </div>
      </button>
    )
  }
)

ChipTrigger.displayName = 'ChipTrigger'
